import React, { useEffect, useRef, useState, useCallback } from "react";
import ScrollReveal from "../premium/ScrollReveal";
import "./SolutionSection.css";

interface Mod {
    id: string; title: string; desc: string; image: string; srcSet?: string;
}

export const MODULES: readonly Mod[] = Object.freeze([
    { id: "centralization", title: "Site Centralization Hub", desc: "One command center for your entire network. Standardize SOPs, automate document flow, and coordinate multi-country operations as if it were a single site.", image: "/images/site.png" },
    { id: "matching", title: "Intelligent Site & Patient Matching", desc: "The engine reads your protocol to surface high-performing sites and eligible populations—before kickoff.", image: "/images/matching.png" },
    { id: "compliance", title: "Automatic Regulatory Compliance", desc: "A single LATAM compliance layer with country presets (COFEPRIS, ANVISA, INVIMA, and more). Submissions, version control, and deadlines handled automatically.", image: "/images/compliance.png" },
    { id: "payments", title: "Automated Milestone Payments", desc: "Evidence-linked payouts: when a milestone is verified, payment fires. No spreadsheets, no reconciliation—just programmable trust.", image: "/images/payments.png" },
    { id: "recruitment", title: "Patient Recruitment & Follow-up", desc: "Autonomous agents handle screening, eConsent, and reminders. Enrollment moves and adherence stays high—without adding headcount. Automatic follow-up keeps patients on protocol.", image: "/images/recruitment.png" },
    { id: "insights", title: "Real-Time Strategic Insights", desc: "Live operational and clinical telemetry with predictive alerts. Anticipate risk, act once, and keep every study on track.", image: "/images/insights.png" },
] as const);

const SolutionSection: React.FC = () => {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const slidesRef = useRef<(HTMLDivElement | null)[]>([]);
    const prefersReducedMotion = useRef(false);

    const [active, setActive] = useState(0);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (typeof window !== "undefined" && "matchMedia" in window) {
            prefersReducedMotion.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        }
    }, []);

    useEffect(() => {
        const root = scrollerRef.current; if (!root) return;
        if (typeof IntersectionObserver === "undefined") { setActive(0); return; }
        const io = new IntersectionObserver((entries) => {
            for (const en of entries) {
                const idx = Number((en.target as HTMLElement).dataset.index);
                if (en.isIntersecting && en.intersectionRatio > 0.6) setActive(idx);
            }
        }, { root, threshold: [0.6] });
        slidesRef.current.forEach(el => el && io.observe(el));
        return () => io.disconnect();
    }, []);

    useEffect(() => {
        const root = scrollerRef.current; if (!root) return;
        const onScroll = () => {
            const max = root.scrollWidth - root.clientWidth;
            const p = max > 0 ? root.scrollLeft / max : 0;
            setProgress(p < 0 ? 0 : p > 1 ? 1 : p);
        };
        onScroll();
        root.addEventListener("scroll", onScroll, { passive: true });
        return () => root.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const root = scrollerRef.current; if (!root) return;
        const onWheel = (e: WheelEvent) => {
            if (window.innerWidth <= 820) return;

            const maxScroll = root.scrollWidth - root.clientWidth;
            if (maxScroll <= 0) return;

            // Solo interceptar si es scroll horizontal predominante
            const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);
            const horizontalDelta = e.deltaX;
            const verticalDelta = e.deltaY;

            // Si es scroll vertical predominante, dejar que pase normalmente
            if (!isHorizontalScroll && Math.abs(verticalDelta) > Math.abs(horizontalDelta)) {
                return;
            }

            // Para scroll horizontal o cuando deltaY es usado como horizontal (rueda del mouse)
            const delta = isHorizontalScroll ? horizontalDelta : verticalDelta;

            const atStart = root.scrollLeft <= 0;
            const atEnd = root.scrollLeft >= maxScroll - 1;

            // Solo prevenir si estamos haciendo scroll horizontal dentro del carrusel
            if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;

            // Solo prevenir el default si vamos a usar el scroll
            e.preventDefault();
            root.scrollTo({ left: root.scrollLeft + delta * 0.9, behavior: "auto" });
        };

        root.addEventListener("wheel", onWheel, { passive: false });
        return () => root.removeEventListener("wheel", onWheel);
    }, []);

    const scrollToIndex = useCallback((i: number, focus?: boolean) => {
        const root = scrollerRef.current; const el = slidesRef.current[i]; if (!root || !el) return;
        const left = el.offsetLeft - (root.clientWidth - el.clientWidth) / 2;
        root.scrollTo({ left, behavior: prefersReducedMotion.current ? "auto" : "smooth" });
        if (focus) requestAnimationFrame(() => el.focus({ preventScroll: true }));
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight") scrollToIndex(Math.min(MODULES.length - 1, active + 1));
            else if (e.key === "ArrowLeft") scrollToIndex(Math.max(0, active - 1));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, scrollToIndex]);

    const handlePrev = useCallback(() => scrollToIndex(Math.max(0, active - 1), true), [active, scrollToIndex]);
    const handleNext = useCallback(() => scrollToIndex(Math.min(MODULES.length - 1, active + 1), true), [active, scrollToIndex]);

    return (
        <section className="sol-hscroll full-bleed" aria-labelledby="solution-modules-heading" role="region">
            <ScrollReveal className="sol-header">
                <span className="sol-eyebrow">SIX POWERFUL MODULES WORKING AS ONE</span>
                <h2 id="solution-modules-heading" className="sol-title">
                    The complete <span className="sol-title-accent">AI-native</span> clinical infrastructure
                </h2>
                <p className="sol-subtitle">Each module tackles a core trial challenge—together they become a single, high-leverage system for LATAM.</p>
            </ScrollReveal>

            <div className="sol-hscroll__pin">
                <div className="sol-hscroll__scroller" ref={scrollerRef} aria-label="Module carousel" role="group">
                    <div className="sol-hscroll__track">
                        {MODULES.map((m, i) => (
                            <div
                                key={m.id}
                                className={`sol-slide ${i === active ? "is-active" : ""}`}
                                ref={(el) => (slidesRef.current[i] = el)}
                                data-index={i}
                                tabIndex={i === active ? 0 : -1}
                                aria-roledescription="slide"
                                aria-label={`${m.title} (${i + 1} of ${MODULES.length})`}
                            >
                                <article className="sol-card glass" role="group" aria-labelledby={`${m.id}-title`}>
                                    <div className="sol-card__media" aria-hidden="true">
                                        <img src={m.image} srcSet={m.srcSet} alt={m.title} loading="lazy" decoding="async" />
                                        <span className="sol-card__glow" />
                                    </div>
                                    <div className="sol-card__body">
                                        <h3 id={`${m.id}-title`} className="sol-card__title">{m.title}</h3>
                                        <p className="sol-card__desc">{m.desc}</p>
                                    </div>
                                </article>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="sol-hscroll__controls" aria-label="Carousel navigation">
                    <button type="button" className="sol-arrow" aria-label="Previous module" onClick={handlePrev} disabled={active === 0}>←</button>
                    <div className="sol-dots" role="tablist" aria-label="Modules list">
                        {MODULES.map((m, i) => (
                            <button
                                key={m.id}
                                role="tab"
                                type="button"
                                aria-selected={i === active}
                                aria-current={i === active || undefined}
                                className={`sol-dot ${i === active ? "is-active" : ""}`}
                                onClick={() => scrollToIndex(i, true)}
                                aria-label={`Go to ${m.title}`}
                            />
                        ))}
                    </div>
                    <button type="button" className="sol-arrow" aria-label="Next module" onClick={handleNext} disabled={active === MODULES.length - 1}>→</button>
                </div>

                <div className="sol-progress" aria-hidden="true">
                    <span className="sol-progress__bar" style={{ width: `${progress * 100}%` }} />
                </div>
            </div>
        </section>
    );
};

export default SolutionSection;